import { useMemo } from "react";

import { Result } from "@bridgesplit/utils";

import { usePartnersQuery } from "../reducers";
import { MetadataTag, PartnerInfoExpanded, PartnerType, PartnerInfo } from "../types";
import { useAbfTypesToUiConverter } from "../utils/ui";

export function usePartnersInfo(): { partners: PartnerInfoExpanded[] | undefined; isLoading: boolean } {
    const { data: partnerList, isLoading: partnersLoading } = usePartnersQuery();
    const { convertPartnerInfo } = useAbfTypesToUiConverter();

    const partners = useMemo(() => {
        if (!partnerList) return undefined;

        const validPartners = partnerList.data.map((rawPartner: PartnerInfo): PartnerInfoExpanded => {
            // First, convert/normalize the backend data
            const partnerInfo = convertPartnerInfo(rawPartner);

            // Then explicitly build the expanded type with frontend computed fields
            const partnerInfoExpanded: PartnerInfoExpanded = {
                ...partnerInfo,
                // Frontend computed fields - explicitly shown as enrichment
                partnerTags: extractPartnerTags(partnerInfo.partner.tags),
                metadataTags: extractMetadataTags(partnerInfo.partner.tags)
            };

            return partnerInfoExpanded;
        });

        return validPartners;
    }, [partnerList, convertPartnerInfo]);

    return { partners, isLoading: partnersLoading };
}

function extractTags<T extends string>(tags: string | undefined, validTags: readonly T[]): T[] {
    if (!tags) return [];
    try {
        const parsed = JSON.parse(tags);
        if (!Array.isArray(parsed)) return [];
        return Array.from(new Set(parsed.filter((tag: string) => validTags.includes(tag as T)))) as T[];
    } catch {
        Result.err("Invalid ecosystemtag format: " + tags);
        return [];
    }
}

function extractPartnerTags(tags: string | undefined): PartnerType[] {
    return extractTags(tags, Object.values(PartnerType));
}

function extractMetadataTags(tags: string | undefined): MetadataTag[] {
    return extractTags(tags, Object.values(MetadataTag));
}
